import {
  Button,
  Divider,
  HStack,
  Heading,
  IconButton,
  InputGroup,
  InputLeftElement,
  NumberInput,
  NumberInputField,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  Portal,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  faArrowRight,
  faArrowsDownToLine,
  faCheck,
  faChevronDown,
  faCircleDot,
  faDiamond,
  faDrawPolygon,
  faMagicWandSparkles,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useFlag } from '@unleash/proxy-client-react';
import { useCallback, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RectangleIcon } from '../../assets/icons/draw-rectangle.svg';
import { ReactComponent as ExtrudeIcon } from '../../assets/icons/extrude-icon.svg';
import { useSheetViewerV2 } from '../viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import SparkelIcon from '../common/icon/SparkelIcon';
import { useUserTenant } from '../../services/auth-info';
import { useProtanProjectSteps } from '../common/ProtanProjectStepsProvider';
import {
  OrderDeepFragment,
  ProtanProjectStepEnum,
  ProtanProjectStepStatusEnum,
} from '../../gql/graphql';
import { DrawingMode } from '../viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import { ActiveTool } from '../viewers/SheetsViewerV2/enums/ActiveTool';
import { useInsulationHandler } from './pdf-shapes/protan/hooks/useInsulationHandler';
import { Substrate } from './ProductChoices';
import { useOrderLabelsProtan } from 'src/hooks/protan-data';

// eslint-disable-next-line complexity
export default function ProtanSheetViewerV2ShapesToolbar({
  order,
}: {
  order: OrderDeepFragment;
}) {
  const { t } = useTranslation('protan');
  const enableMagicSheetShapes = useFlag('enable-magic-sheet-shapes');

  const {
    activeSheet,
    activeTool,
    setActiveTool,
    drawing: {
      isDrawing,
      drawingMode,
      setDrawingMode,
      createShape,
      createShapeResult,
      hasResults,
      results,
      updateShape,
      updateShapeResult,
      shapeToEdit,
    },
  } = useSheetViewerV2();

  const { substrate } = useOrderLabelsProtan();
  const backgroundColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const activeIconColor = useColorModeValue('teal.500', 'teal.200');

  const [hasAppliedWedges, setHasAppliedWedges] = useState(false);

  const user = useUserTenant();
  const { projectSteps } = useProtanProjectSteps();
  const {
    isOpen: isProtanShapesPopoverOpen,
    onClose: closeProtanShapesPopover,
  } = useDisclosure({
    defaultIsOpen: true,
  });

  const roofOutlineInProgress =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.RoofOutline
    )?.status === ProtanProjectStepStatusEnum.InProgress;

  const drainLinePlacementInProgress =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.DrainLinePlacement
    )?.status === ProtanProjectStepStatusEnum.InProgress;

  const drainPlacementInProgress =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.DrainPlacement
    )?.status === ProtanProjectStepStatusEnum.InProgress;

  const wedgePlacementInProgress =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.WedgePlacement
    )?.status === ProtanProjectStepStatusEnum.InProgress;

  const shouldShowToolbarPopover =
    isProtanShapesPopoverOpen &&
    user.isProtan &&
    (roofOutlineInProgress ||
      drainLinePlacementInProgress ||
      drainPlacementInProgress ||
      wedgePlacementInProgress);

  let protanPopoverHeaderText = t('shapes-popover.roof-outline.title');
  let protanPopoverBodyText = t('shapes-popover.roof-outline.description');

  if (drainLinePlacementInProgress) {
    protanPopoverHeaderText = t('shapes-popover.drainage-line.title');
    protanPopoverBodyText = t('shapes-popover.drainage-line.description');
  } else if (
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.DrainPlacement
    )?.status === ProtanProjectStepStatusEnum.InProgress
  ) {
    protanPopoverHeaderText = t('shapes-popover.wedges.title');
    protanPopoverBodyText = t('shapes-popover.wedges.description');
  } else if (
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.WedgePlacement
    )?.status === ProtanProjectStepStatusEnum.InProgress
  ) {
    protanPopoverHeaderText = t('shapes-popover.wedges.title');
    protanPopoverBodyText = t('shapes-popover.wedges.description');
  }

  // Create a wrapper function that maps V1 ShapeDrawingMode to V2 DrawingMode
  const activateDrawingV2 = useCallback(
    (mode: any) => {
      switch (mode) {
        case 'wedge':
          setActiveTool(ActiveTool.Wedge);
          break;
        case 'drain-line':
          setActiveTool(ActiveTool.DrainLine);
          break;
        case 'line-string':
          setActiveTool(ActiveTool.Drawing);
          setDrawingMode(DrawingMode.Polyline);
          break;
        case 'point':
          setActiveTool(ActiveTool.Drawing);
          setDrawingMode(DrawingMode.Point);
          break;
        case 'polygon':
          setActiveTool(ActiveTool.Drawing);
          setDrawingMode(DrawingMode.Polygon);
          break;
        case 'rectangle':
          setActiveTool(ActiveTool.Drawing);
          setDrawingMode(DrawingMode.Rectangle);
          break;
        case 'magic-polygon':
          setActiveTool(ActiveTool.Drawing);
          setDrawingMode(DrawingMode.MagicPolygon);
          break;
        default:
          setActiveTool(ActiveTool.Drawing);
          setDrawingMode(DrawingMode.Polygon);
      }
    },
    [setActiveTool, setDrawingMode]
  );

  const { handleUseFlatInsulation } = useInsulationHandler(
    order,
    activateDrawingV2
  );

  // Submit drawing function adapted for V2
  const submitDrawing = useCallback(async () => {
    if (!hasResults || !activeSheet || !activeSheet.data || !results) return;

    if (shapeToEdit) {
      await updateShape();
    } else {
      // For V2, we use the same createShape function as the regular V2 toolbar
      // The shape creation logic is handled by the useShapesDrawingTool hook
      // The createShape function is already available from the useSheetViewerV2 hook
    }

    setActiveTool(ActiveTool.Default);
  }, [
    hasResults,
    activeSheet,
    results,
    shapeToEdit,
    updateShape,
    setActiveTool,
  ]);

  useHotkeys('esc', () => setActiveTool(ActiveTool.Default), {
    enabled: isDrawing,
  });
  useHotkeys('enter', () => submitDrawing(), [submitDrawing], {
    enabled:
      isDrawing &&
      hasResults &&
      !createShapeResult.fetching &&
      !updateShapeResult.fetching,
  });
  useHotkeys(
    'space',
    async () => {
      await submitDrawing();
      // Without this, the drawing tool doesn't have time to clear the previous drawing
      setTimeout(() => {
        // Restart with the same tool that was active
        if (activeTool === ActiveTool.Wedge) {
          setActiveTool(ActiveTool.Wedge);
        } else if (activeTool === ActiveTool.DrainLine) {
          setActiveTool(ActiveTool.DrainLine);
        } else {
          setActiveTool(ActiveTool.Drawing);
        }
      }, 10);
    },
    {
      enabled:
        isDrawing &&
        hasResults &&
        !createShapeResult.fetching &&
        !updateShapeResult.fetching &&
        !!activeSheet &&
        !!activeSheet.data,
    },
    [submitDrawing, setActiveTool, activeTool]
  );
  useHotkeys(
    'shift+s',
    () => setActiveTool(ActiveTool.Drawing),
    [setActiveTool],
    {
      enabled: !!activeSheet && !!activeSheet.data,
    }
  );

  if (!isDrawing) {
    return null;
  }

  return (
    <Popover
      isOpen={shouldShowToolbarPopover}
      onClose={closeProtanShapesPopover}
      autoFocus={false}
    >
      <PopoverAnchor>
        <HStack
          backgroundColor={backgroundColor}
          rounded={50}
          px={1}
          height={8}
          borderColor={borderColor}
          borderWidth="1px"
          mb={1}
        >
          {drainLinePlacementInProgress && (
            <>
              <Tooltip label={t('toolbar.drainage-line')}>
                <IconButton
                  variant={'ghost'}
                  colorScheme={'teal'}
                  aria-label="draw drainage line"
                  size="xs"
                  icon={
                    <SparkelIcon
                      icon={faChevronDown}
                      color={
                        activeTool === ActiveTool.DrainLine
                          ? activeIconColor
                          : undefined
                      }
                    />
                  }
                  isActive={activeTool === ActiveTool.DrainLine}
                  isDisabled={!!shapeToEdit}
                  onClick={() => {
                    setActiveTool(ActiveTool.DrainLine);
                  }}
                />
              </Tooltip>
              <InputGroup size={'xs'} width={24}>
                <InputLeftElement>
                  <SparkelIcon as={ExtrudeIcon} color={activeIconColor} />
                </InputLeftElement>
                <NumberInput
                  variant={'filled'}
                  max={1000}
                  min={80} // Stop from setting zero-thickness
                  color={activeIconColor}
                  precision={0}
                  step={15}
                  defaultValue={1000}
                  onChange={(value) => {
                    const mm = parseFloat(value);
                    // First subtract base, then round up to nearest 60mm, then add base back
                    const base = substrate === Substrate.Concrete ? 50 : 80;
                    const roundedMm =
                      base + Math.ceil(Math.max(0, mm - base) / 15) * 15;
                    // Note: minHeight handling would need to be implemented for V2
                    console.log('Min height set to:', roundedMm / 1000);
                  }}
                >
                  <NumberInputField
                    paddingLeft={6}
                    paddingRight={0}
                    borderRadius={'md'}
                    autoFocus
                    width={24}
                    backgroundColor={borderColor}
                    _hover={{ backgroundColor: 'blackAlpha.500' }}
                  />
                </NumberInput>
              </InputGroup>
            </>
          )}
          {drainPlacementInProgress && (
            <>
              <Tooltip label={t('toolbar.wedge')}>
                <IconButton
                  variant={'ghost'}
                  colorScheme={'teal'}
                  aria-label="place wedge"
                  size="xs"
                  icon={
                    <SparkelIcon
                      icon={faDiamond}
                      color={
                        activeTool === ActiveTool.Wedge
                          ? activeIconColor
                          : undefined
                      }
                    />
                  }
                  isActive={activeTool === ActiveTool.Wedge}
                  isDisabled={!!shapeToEdit}
                  onClick={() => {
                    setActiveTool(ActiveTool.Wedge);
                  }}
                />
              </Tooltip>
              <Tooltip label={t('toolbar.drain')}>
                <IconButton
                  variant={'ghost'}
                  colorScheme={'teal'}
                  aria-label="place drain"
                  size="xs"
                  icon={
                    <SparkelIcon
                      icon={faCircleDot}
                      color={
                        drawingMode === DrawingMode.Point
                          ? activeIconColor
                          : undefined
                      }
                    />
                  }
                  isActive={drawingMode === DrawingMode.Point}
                  isDisabled={!!shapeToEdit}
                  onClick={() => {
                    setDrawingMode(DrawingMode.Point);
                  }}
                />
              </Tooltip>
            </>
          )}
          {wedgePlacementInProgress && (
            <Tooltip label={t('toolbar.wedge')}>
              <IconButton
                variant={'ghost'}
                colorScheme={'teal'}
                aria-label="place wedge"
                size="xs"
                icon={
                  <SparkelIcon
                    icon={faDiamond}
                    color={
                      activeTool === ActiveTool.Wedge
                        ? activeIconColor
                        : undefined
                    }
                  />
                }
                isActive={activeTool === ActiveTool.Wedge}
                isDisabled={!!shapeToEdit}
                onClick={() => {
                  setActiveTool(ActiveTool.Wedge);
                }}
              />
            </Tooltip>
          )}
          {roofOutlineInProgress && (
            <Tooltip label={t('toolbar.rectangle')}>
              <IconButton
                variant={'ghost'}
                colorScheme={'teal'}
                aria-label="draw rectangle"
                size="xs"
                icon={
                  <SparkelIcon
                    as={RectangleIcon}
                    color={
                      drawingMode === DrawingMode.Rectangle
                        ? activeIconColor
                        : undefined
                    }
                  />
                }
                isActive={drawingMode === DrawingMode.Rectangle}
                isDisabled={!!shapeToEdit}
                onClick={() => {
                  setDrawingMode(DrawingMode.Rectangle);
                }}
              />
            </Tooltip>
          )}
          {projectSteps?.find(
            (step) => step.step === ProtanProjectStepEnum.RoofOutline
          )?.status === ProtanProjectStepStatusEnum.InProgress && (
            <Tooltip label={t('toolbar.polygon')}>
              <IconButton
                variant={'ghost'}
                colorScheme={'teal'}
                aria-label="draw polygon"
                size="xs"
                icon={
                  <SparkelIcon
                    icon={faDrawPolygon}
                    color={
                      drawingMode === DrawingMode.Polygon
                        ? activeIconColor
                        : undefined
                    }
                  />
                }
                isActive={drawingMode === DrawingMode.Polygon}
                isDisabled={!!shapeToEdit}
                onClick={() => {
                  setDrawingMode(DrawingMode.Polygon);
                }}
              />
            </Tooltip>
          )}
          {enableMagicSheetShapes &&
            projectSteps?.find(
              (step) => step.step === ProtanProjectStepEnum.RoofOutline
            )?.status === ProtanProjectStepStatusEnum.InProgress && (
              <Tooltip label={t('toolbar.magic-polygon')}>
                <IconButton
                  variant={'ghost'}
                  colorScheme={'teal'}
                  aria-label="draw magic polygon"
                  size="xs"
                  icon={<SparkelIcon icon={faMagicWandSparkles} />}
                  isActive={drawingMode === DrawingMode.MagicPolygon}
                  isDisabled={!!shapeToEdit}
                  onClick={() => {
                    setDrawingMode(DrawingMode.MagicPolygon);
                  }}
                />
              </Tooltip>
            )}
          <Divider orientation="vertical" height={'70%'} />
          {drainLinePlacementInProgress ? (
            <Button
              aria-label={t('toolbar.use-flat-insulation')}
              size="xs"
              colorScheme="gray"
              onClick={handleUseFlatInsulation}
              rightIcon={
                <SparkelIcon icon={faArrowsDownToLine} color={'inherit'} />
              }
            >
              {t('toolbar.use-flat-insulation')}
            </Button>
          ) : (
            <Tooltip label={t('toolbar.cancel-drawing')}>
              <IconButton
                aria-label="Cancel drawing"
                colorScheme="gray"
                size="xs"
                onClick={() => setActiveTool(ActiveTool.Default)}
                isRound
                icon={<SparkelIcon icon={faTimes} />}
              ></IconButton>
            </Tooltip>
          )}
          {drawingMode === DrawingMode.Polyline && (
            <Tooltip label={hasResults || t('toolbar.create-valid-shape')}>
              <Button
                aria-label={t('toolbar.next')}
                size="xs"
                onClick={async () => {
                  await submitDrawing();
                  setActiveTool(ActiveTool.Drawing);
                  setHasAppliedWedges(true);
                }}
                colorScheme="gray"
                isLoading={
                  createShapeResult.fetching || updateShapeResult.fetching
                }
                isDisabled={!hasResults}
                rightIcon={<SparkelIcon icon={faCheck} color={'inherit'} />}
              >
                Apply wedges
              </Button>
            </Tooltip>
          )}
          <Tooltip label={hasResults || t('toolbar.create-valid-shape')}>
            <Button
              aria-label={t('toolbar.next')}
              size="xs"
              onClick={() => submitDrawing()}
              isLoading={
                createShapeResult.fetching || updateShapeResult.fetching
              }
              isDisabled={
                drawingMode === DrawingMode.Polyline
                  ? !hasAppliedWedges
                  : !hasResults
              }
              rightIcon={
                <SparkelIcon
                  icon={faArrowRight}
                  color={['gray.50', 'gray.700']}
                />
              }
            >
              {t('toolbar.next')}
            </Button>
          </Tooltip>
        </HStack>
      </PopoverAnchor>

      <Portal>
        <PopoverContent backgroundColor={'blue.100'} color={'blue.900'}>
          <PopoverCloseButton onClick={closeProtanShapesPopover} />
          <PopoverHeader borderBottom={'none'}>
            <Heading size="sm" as="h3" color={'inherit'}>
              {protanPopoverHeaderText}
            </Heading>
          </PopoverHeader>
          <PopoverBody>{protanPopoverBodyText}</PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

// Protan specific flow for drawing shapes adapted for V2.
// If roof outline is in progress and drain placement is not started, set roof outline to completed and drain placement to in progress
// If roof outline and drain placement is in progress, set drain placement to completed
// eslint-disable-next-line complexity
export const handleProtanFlowV2 = async (
  setActiveTool: (tool: ActiveTool) => void,
  setDrawingMode: (mode: DrawingMode) => void,
  projectSteps:
    | {
        step: ProtanProjectStepEnum;
        status: ProtanProjectStepStatusEnum;
      }[]
    | undefined,
  setGhostMode: (ghostMode: number) => void,
  setStepStatus: (
    step: ProtanProjectStepEnum,
    status: ProtanProjectStepStatusEnum
  ) => void,
  fireRequirements: string | undefined,
  handleUseFlatInsulation: () => Promise<void>
) => {
  const roofOutlineStatus =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.RoofOutline
    )?.status || ProtanProjectStepStatusEnum.NotStarted;

  const drainLinePlacementStatus =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.DrainLinePlacement
    )?.status || ProtanProjectStepStatusEnum.NotStarted;

  const drainPlacementStatus =
    projectSteps?.find(
      (step) => step.step === ProtanProjectStepEnum.DrainPlacement
    )?.status || ProtanProjectStepStatusEnum.NotStarted;

  if (
    roofOutlineStatus === ProtanProjectStepStatusEnum.InProgress &&
    drainLinePlacementStatus === ProtanProjectStepStatusEnum.NotStarted
  ) {
    // First, if roof outline was in progress and drain line placement was not started,
    // set roof outline to completed and drain line placement to in progress
    setStepStatus(
      ProtanProjectStepEnum.RoofOutline,
      ProtanProjectStepStatusEnum.Completed
    );
    if (fireRequirements === 'Combustible') {
      // Skip drain line placement if fire requirements are combustible
      // (we don't support tapered insulation for combustible roofs)
      console.log('Skipping drain line placement');
      await handleUseFlatInsulation();
    } else {
      // Set drain line placement to in progress
      setStepStatus(
        ProtanProjectStepEnum.DrainLinePlacement,
        ProtanProjectStepStatusEnum.InProgress
      );
      setActiveTool(ActiveTool.Drawing);
      setDrawingMode(DrawingMode.Polyline);
    }
  } else if (
    // If roof outline was completed and drain line placement was in progress,
    // set drain line placement to completed and drain placement to in progress
    roofOutlineStatus === ProtanProjectStepStatusEnum.Completed &&
    drainLinePlacementStatus === ProtanProjectStepStatusEnum.InProgress
  ) {
    setStepStatus(
      ProtanProjectStepEnum.DrainLinePlacement,
      ProtanProjectStepStatusEnum.Completed
    );
    setStepStatus(
      ProtanProjectStepEnum.DrainPlacement,
      ProtanProjectStepStatusEnum.InProgress
    );
    setActiveTool(ActiveTool.Drawing);
    setDrawingMode(DrawingMode.Polyline);
  } else if (
    drainLinePlacementStatus === ProtanProjectStepStatusEnum.Completed &&
    drainPlacementStatus === ProtanProjectStepStatusEnum.InProgress
  ) {
    // If drain line placement was completed and drain placement is in progress,
    // set drain placement to completed

    setGhostMode(1); // Make sure ghosting is enabled

    setStepStatus(
      ProtanProjectStepEnum.DrainPlacement,
      ProtanProjectStepStatusEnum.Completed
    );

    // Set wedges to completed for now, but should be set to in progress when the user starts drawing the wedges
    setStepStatus(
      ProtanProjectStepEnum.WedgePlacement,
      ProtanProjectStepStatusEnum.Completed
    );
  }
};
